<template>
  <div class="kent-id-card-report">
    <!-- Control Bar -->
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <div class="brand-badge mr-2">
          <feather-icon icon="ShieldIcon" size="20" class="text-white" />
        </div>
        <h4 class="mb-0 font-weight-bolder">
          Kent School <span class="text-primary">| ID Card Generator</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2 px-1">
          {{ reportItems.length }} Students Loaded
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print All Cards</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Exit</span>
        </button>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5 loading-wrapper">
      <div class="loader-content">
        <b-spinner
          variant="primary"
          style="width: 4rem; height: 4rem"
          class="mb-3"
        />
        <h3 class="font-weight-bold text-primary">
          Generating Kent School ID Cards
        </h3>
        <p class="text-muted">
          Please wait while we process the student records...
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="text-center py-5 error-wrapper">
      <div class="error-icon-bg mb-3">
        <feather-icon icon="AlertTriangleIcon" size="48" class="text-danger" />
      </div>
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button
        variant="outline-primary"
        @click="loadReportData"
        class="mt-3 round"
        >Try Again</b-button
      >
    </div>

    <!-- Cards Container -->
    <div v-else>
      <div
        v-if="reportItems.length === 0"
        class="text-center py-5 no-data-state"
      >
        <div class="no-data-icon">
          <feather-icon icon="InboxIcon" size="64" />
        </div>
        <h3 class="mt-2 font-weight-bold">No Students Found</h3>
        <p class="text-muted">
          Try adjusting your filters to find student records.
        </p>
      </div>

      <div v-else class="print-pages">
        <div
          v-for="(page, pageIndex) in cardPages"
          :key="'page-' + pageIndex"
          class="print-page"
          :class="`print-page--count-${page.length}`"
        >
          <div class="cards-grid">
            <div
              v-for="(student, index) in page"
              :key="student.id || student.grNo || `${pageIndex}-${index}`"
              class="student-id-block"
            >
              <!-- Front Side -->
              <div class="id-card id-card--front">
                <div class="card-brand-header">
                  <div class="card-brand-header__left">
                    <div class="kent-logo-badge">
                      <img
                        :src="kentLogo"
                        alt="Kent Schooling System"
                        class="card-brand-header__logo"
                      />
                    </div>
                    <div class="card-brand-header__brand">
                      <span class="card-brand-header__school">{{
                        headerSchoolName
                      }}</span>
                      <span class="card-brand-header__campus">{{
                        headerCampusName
                      }}</span>
                    </div>
                  </div>
                </div>

                <div class="card-body-front">
                  <div class="student-profile">
                    <div class="photo-ring">
                      <img
                        :src="studentPhotoUrl(student)"
                        :alt="studentDisplayName(student)"
                        class="photo-ring__img"
                        referrerpolicy="no-referrer"
                        @error="handleImgError($event, student)"
                      />
                    </div>

                    <h3 class="student-name">
                      {{ studentDisplayName(student) }}
                    </h3>
                  </div>

                  <div class="info-table">
                    <div class="info-table__row">
                      <span class="info-table__label">GR No.</span>
                      <span class="info-table__value">{{
                        student.grNo || student.gr || "—"
                      }}</span>
                    </div>
                    <div class="info-table__row">
                      <span class="info-table__label">Father's Name</span>
                      <span class="info-table__value">{{
                        student.father_name || student.fatherName || "—"
                      }}</span>
                    </div>
                    <div class="info-table__row">
                      <span class="info-table__label">Class</span>
                      <span class="info-table__value">{{
                        formatClass(student)
                      }}</span>
                    </div>
                  </div>

                  <div class="qr-block">
                    <qrcode-vue
                      :value="qrValue(student)"
                      :size="64"
                      level="H"
                      class="qr-block__code"
                    />
                    <span class="qr-block__label">SCAN TO VERIFY</span>
                  </div>
                </div>

                <div class="card-brand-footer">Student Card</div>
              </div>

              <!-- Back Side -->
              <div class="id-card id-card--back">
                <div class="back-inner">
                  <div class="back-logo-wrap">
                    <img
                      :src="kentLogo"
                      alt="Kent Schooling System"
                      class="back-logo-wrap__img"
                    />
                  </div>

                  <div class="back-contact">
                    <p v-if="campusPhone" class="back-contact__phone">
                      {{ campusPhone }}
                    </p>
                    <p class="back-contact__address">
                      {{ campusAddress || "—" }}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import axios from "axios";
import QrcodeVue from "qrcode.vue";
import {
  getMediaPreviewUrl,
  handleDrivePreviewError,
} from "@/utils/gdriveUpload";

export default {
  name: "KentIDCard",
  components: {
    BButton,
    BSpinner,
    BBadge,
    QrcodeVue,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      kentLogo: "/kennt_logo.png",
      schoolName: "",
      campusName: "",
      campusAddress: "",
      campusPhone: "",
      db: "",
      cID: "",
      classID: 0,
      sectionID: 0,
      status: "present",
      sessYear: "",
      sessionID: 0,
      studentIDs: [],
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
      defaultPlaceholder:
        "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23e8ecf0'/%3E%3Ccircle cx='50' cy='38' r='20' fill='%23adb5bd'/%3E%3Cpath d='M10 100 Q10 68 50 68 Q90 68 90 100Z' fill='%23adb5bd'/%3E%3C/svg%3E",
    };
  },
  computed: {
    headerSchoolName() {
      return (this.schoolName || "Kent Schooling System").trim();
    },
    headerCampusName() {
      return (this.campusName || "Campus").trim();
    },
    cardPages() {
      const pageSize = 5;
      const pages = [];
      const items = this.reportItems || [];

      for (let i = 0; i < items.length; i += pageSize) {
        pages.push(items.slice(i, i + pageSize));
      }

      return pages;
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    initReport() {
      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = query.cID || 0;
      this.classID = query.cls || "";
      this.sectionID = query.sec || "";
      if (query.studentIDs) {
        this.studentIDs = query.studentIDs
          .split(",")
          .map((id) => parseInt(id, 10))
          .filter((id) => !isNaN(id));
      }

      this.status =
        this.studentIDs.length > 0 ? "all" : query.status || "present";
      this.sessYear = query.sess || "2026-2027";
      this.sessionID = query.sessionID || 0;

      if (!this.db) {
        this.error = "System context missing. Please reload from dashboard.";
        return;
      }

      this.loadCampusInfo();
      this.loadReportData();
    },
    async loadCampusInfo() {
      try {
        const url = "https://apiv26.myskool.app/api/campuses";
        const response = await axios.get(url, {
          params: { db: this.db, cId: this.cID },
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
            accept: "application/json",
          },
        });

        if (
          response.data &&
          response.data.status === "success" &&
          Array.isArray(response.data.data)
        ) {
          const match = response.data.data.find((c) => c.id == this.cID);
          if (match) {
            this.schoolName = match.organizationName || match.orgName || "";
            this.campusName = match.branch || match.name || "";
            this.campusAddress = match.address || match.branch || "";
            this.campusPhone =
              match.phone || match.contact || match.mobile || "";
          }
        }
      } catch (e) {
        console.warn("Campus branding failed to load");
      }
    },
    formatClass(student) {
      if (!student) return "—";
      if (student.meta_class) {
        return String(student.meta_class).trim() || "—";
      }
      const cls = student.className || student.cls || "";
      const sec =
        student.sectionName || student.section || student.secName || "";
      if (cls && sec) return `${cls} - ${sec}`;
      return cls || sec || "—";
    },
    qrValue(student) {
      return String(student.id || student.grNo || student.gr || "");
    },
    normalizeStudentList(responseData) {
      if (!responseData) return [];
      if (!Array.isArray(responseData) && Array.isArray(responseData.data)) {
        return responseData.data;
      }
      if (Array.isArray(responseData)) return responseData;
      return [];
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      const headers = {
        Authorization: `Bearer ${this.$store.state.userData.token}`,
        accept: "application/json",
      };

      const sessionID = this.sessionID || this.$store.state.sessionID || 0;

      try {
        let response;

        if (this.studentIDs.length > 0) {
          const url = `${this.$store.state.domain}Students/LoadDataSelected`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              ids: this.studentIDs.join(","),
              status: "all",
              sessionID,
            },
            headers,
          });
        } else {
          const url = `${this.$store.state.domain}Students/LoadDataAll`;
          response = await axios.get(url, {
            params: {
              db: this.db,
              cID: this.cID,
              clsID: this.classID || 0,
              section: this.sectionID || 0,
              status: this.status,
              sessionID,
            },
            headers,
          });
        }

        if (response.data && response.data.status === "success") {
          this.reportItems = this.normalizeStudentList(response.data.data);
        } else if (Array.isArray(response.data)) {
          this.reportItems = response.data;
        } else {
          this.error =
            response.data?.message ||
            "The server did not return valid student records.";
        }
      } catch (err) {
        console.error("Data retrieval error:", err);
        this.error = "Unable to connect to school records system.";
      } finally {
        this.loading = false;
      }
    },
    studentPhotoUrl(student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (!url) return this.defaultPlaceholder;
      if (String(url).startsWith("blob:")) return url;
      return getMediaPreviewUrl(url, "Image");
    },
    studentDisplayName(student) {
      const name =
        student?.name || student?.studentName || student?.student_name || "";
      return String(name).trim() || "—";
    },
    handleImgError(e, student) {
      const url = student?.picture || student?.photo || student?.image || "";
      if (url && !String(url).startsWith("blob:")) {
        handleDrivePreviewError(e, url, "image");
        return;
      }
      e.target.src = this.defaultPlaceholder;
    },
    print() {
      window.print();
    },
    close() {
      if (this.studentIDs && this.studentIDs.length > 0) {
        this.$router.push({ name: "manage-studentgrid" });
      } else {
        this.$router.push({ name: "report" });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap");

$brand-blue: #1a2b56;
$back-gray: #c1c1c1;
$myskool-primary: #7367f0;
// Physical card size: 2.2in wide x 3.5in tall (portrait)
$card-w: 2.2in;
$card-h: 3.5in;

.kent-logo-badge {
  background: #ffffff;
  border-radius: 4px;
  padding: 1.5px 2px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 34px;
  height: 34px;
  box-sizing: border-box;

  .card-brand-header__logo {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}

.kent-id-card-report {
  background: #f0f2f5;
  min-height: 100vh;
  padding: 3rem;
  font-family: "Inter", sans-serif;
  color: #2c3e50;
}

.control-bar {
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(10px);
  padding: 1.25rem 2.5rem;
  margin-bottom: 3rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.4);

  .brand-badge {
    background: $myskool-primary;
    padding: 8px;
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.3);
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
}

.btn-modern {
  display: flex;
  align-items: center;
  padding: 0.75rem 1.75rem;
  border-radius: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: none;
  outline: none;
  font-size: 0.9rem;

  &:active {
    transform: scale(0.95);
  }
}

.btn-print {
  background: linear-gradient(135deg, $myskool-primary 0%, #a29bfe 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(115, 103, 240, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(115, 103, 240, 0.4);
  }
}

.btn-close {
  background: white;
  color: #6c757d;
  border: 1px solid #e9ecef;

  &:hover {
    background: #fff;
    color: #ea5455;
    border-color: #fab1a0;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
}

.cards-grid {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: flex-start;
  align-content: flex-start;
  gap: 20px 24px;
  width: 100%;
  margin: 0 auto;
}

.print-pages {
  width: 100%;
}

.print-page + .print-page {
  margin-top: 32px;
}

.student-id-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: $card-w;
  break-inside: avoid;
  page-break-inside: avoid;
}

.id-card {
  width: $card-w;
  height: $card-h;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #fff;
}

.id-card--front {
  border: 1px solid rgba(26, 43, 86, 0.2);
}

.id-card--back {
  background: $back-gray;
  border: 1px solid darken($back-gray, 10%);
}

.card-brand-header {
  display: flex;
  align-items: center;
  min-height: 42px;
  padding: 4px 8px;
  background: $brand-blue;
  color: #fff;
  flex-shrink: 0;

  &__left {
    display: flex;
    align-items: center;
    gap: 5px;
    min-width: 0;
    flex: 1;
  }

  &__logo {
    width: 34px;
    height: 34px;
    object-fit: contain;
    flex-shrink: 0;
  }

  &__brand {
    display: flex;
    flex-direction: column;
    min-width: 0;
    line-height: 1.1;
  }

  &__school {
    font-size: 6.5pt;
    font-weight: 800;
    letter-spacing: 0.02em;
    color: #fff;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 3.4cm;
  }

  &__campus {
    font-size: 5.5pt;
    font-weight: 500;
    opacity: 0.92;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 3.4cm;
  }
}

.card-body-front {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 7px 9px 8px;
  min-height: 0;
  overflow: hidden;
}

.student-profile {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex-shrink: 0;
}

.photo-ring {
  width: 2cm;
  height: 2cm;
  border-radius: 50%;
  border: 2.5px solid $brand-blue;
  overflow: hidden;
  flex-shrink: 0;
  background: #f5f5f5;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);

  &__img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.student-name {
  margin: 4px 0 0;
  font-size: 10pt;
  font-weight: 700;
  color: $brand-blue;
  text-align: center;
  line-height: 1.2;
  max-width: 100%;
  padding: 0 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.info-table {
  width: 100%;
  margin-top: 2px;
  border-top: 1px solid #e0e0e0;

  &__row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 3px 2px;
    border-bottom: 1px solid #e0e0e0;
    font-size: 6.5pt;
    line-height: 1.25;
  }

  &__label {
    color: #888;
    font-weight: 500;
    flex-shrink: 0;
  }

  &__value {
    color: #333;
    font-weight: 600;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 55%;
  }
}

.qr-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  margin-top: auto;
  padding-top: 2px;
  width: 100%;
  flex-shrink: 0;

  &__code {
    display: block;
    margin-bottom: 5px;
  }

  &__label {
    margin: 0 0 6px;
    font-size: 5pt;
    font-weight: 700;
    letter-spacing: 0.12em;
    color: #666;
  }
}

.card-brand-footer {
  flex-shrink: 0;
  height: 22px;
  margin-top: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: $brand-blue;
  color: #fff;
  font-size: 7pt;
  font-weight: 600;
  letter-spacing: 0.06em;
}

.back-inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  padding: 8px 8px 4px;
}

.back-logo-wrap {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 0;

  &__img {
    width: 4.6cm;
    max-width: 94%;
    height: auto;
    max-height: 100%;
    object-fit: contain;
  }
}

.back-contact {
  text-align: center;
  width: 100%;
  flex-shrink: 0;
  margin-top: auto;
  padding-bottom: 2px;

  &__phone {
    margin: 0;
    font-size: 7pt;
    font-weight: 700;
    line-height: 1.1;
    color: $brand-blue;
  }

  &__address {
    margin: 1px 0 0;
    font-size: 5.5pt;
    line-height: 1.12;
    color: rgba(26, 43, 86, 0.88);
    padding: 0 4px;
  }
}

.loading-wrapper,
.error-wrapper,
.no-data-state {
  background: white;
  border-radius: 20px;
  padding: 4rem;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
}

@media print {
  .no-print {
    display: none !important;
  }

  body {
    background: white !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  @page {
    size: A4 landscape;
    margin: 12mm 3mm 4mm 3mm;
  }

  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }

  .kent-id-card-report {
    padding: 0 !important;
    background: white !important;
    min-height: 0 !important;
    height: auto !important;
  }

  .print-pages {
    width: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    height: auto !important;
  }

  .print-page {
    width: 100% !important;
    height: auto !important;
    padding-top: 10mm !important;
    page-break-inside: auto !important;
    break-inside: auto !important;
  }

  .print-page:not(:last-child) {
    page-break-after: always !important;
    break-after: page !important;
  }

  .print-page:last-child {
    page-break-after: auto !important;
    break-after: auto !important;
  }

  .print-page + .print-page {
    margin-top: 0 !important;
  }

  .cards-grid {
    display: grid !important;
    justify-content: center !important;
    justify-items: center !important;
    align-content: start !important;
    width: 100% !important;
    margin: 0 auto !important;
    padding: 0 !important;
    gap: 1.5mm !important;
  }

  @for $i from 1 through 5 {
    .print-page--count-#{$i} .cards-grid {
      grid-template-columns: repeat(#{$i}, $card-w) !important;
    }
  }

  .student-id-block {
    display: flex !important;
    flex-direction: column !important;
    align-items: center !important;
    width: $card-w !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    gap: 1.5mm !important;
    padding: 0 !important;
    margin: 0 !important;
  }

  .card-brand-footer {
    margin-top: 0 !important;
  }

  .id-card {
    width: $card-w !important;
    height: $card-h !important;
    min-width: $card-w !important;
    min-height: $card-h !important;
    max-width: $card-w !important;
    max-height: $card-h !important;
    flex-shrink: 0 !important;
    border-radius: 8px !important;
    overflow: hidden !important;
    box-shadow: none !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  .card-brand-header,
  .card-brand-footer,
  .id-card--back,
  .kent-logo-badge,
  .back-contact__phone,
  .back-contact__address {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
}
</style>

<style lang="scss">
@media print {
  html,
  body,
  #app,
  #app.h-100 {
    height: auto !important;
    min-height: 0 !important;
    overflow: visible !important;
  }
}
</style>
